import { usePreviewActionSavingsAccountQuery } from "@/services/savingsAPI";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  NumberInput,
  Paper,
  rem,
  Text,
  Title,
} from "@mantine/core";
import { get } from "lodash";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import BeneficiaryAccounts from "../../components/BeneficiaryAccounts";
import { toCurrency } from "@/utils/common";
import PopupNoti from "../components/PopupNoti";
import { beText } from "@/utils/beText";

interface Props {
  // third parameter is the availableBalance of the selected beneficiary account
  onSubmit?: (
    beneficiaryAccountNo: string,
    actionAmount: string,
    availableBalance?: number,
  ) => void;
  loadingInit: boolean;
}

const DepositTopupStepOne: React.FC<Props> = ({ onSubmit, loadingInit }) => {
  const { t } = useTranslation();
  const [withdrawAmount, setWithdrawAmount] = useState<number | undefined>(
    undefined,
  );
  const { state } = useLocation();

  const { data: dataTopup } = usePreviewActionSavingsAccountQuery({
    accountNo: state?.accountNo || "",
    action: "DEPOSIT_MANUAL_TOP_UP",
  });
  const dataSources = get(dataTopup, "body.beneficiaryAccounts", []);
  console.log("dataSources", dataSources);

  const sources = useMemo(() => {
    return dataSources || [];
  }, [dataSources]);
  const [sourceLinkId, setSourceLinkId] = useState<string>(
    get(sources, "[0].accountNo", ""),
  );
  const [sourceAvailableBalance, setSourceAvailableBalance] = useState<number>(
    get(sources, "[0].availableBalance", 0),
  );

  const [amountError, setAmountError] = useState<string | null>(null);
  const [popupOpened, { open: openPopup, close: closePopup }] =
    useDisclosure(false);

  const resultWithdraw = get(dataTopup, "body");
  const handleSubmit = () => {
    if (!withdrawAmount || withdrawAmount <= 0) {
      setAmountError(`${t("PLEASE_ENTER_TOPUP_AMOUNT")}`);
      return;
    }

    // check against available balance
    if (withdrawAmount > (sourceAvailableBalance || 0)) {
      openPopup();
      return;
    }

    const beneficiaryAccountNo =
      sources.find((source) => source.accountNo === sourceLinkId)?.accountNo ||
      resultWithdraw?.beneficiaryAccounts?.[0]?.accountNo ||
      "";
    console.log("beneficiaryAccountNo", beneficiaryAccountNo, sourceAvailableBalance);
    onSubmit?.(
      beneficiaryAccountNo,
      withdrawAmount?.toString() || "0",
      sourceAvailableBalance,
    );
  };

  const handleReceiveData = (data: string, balance: number) => {
    setSourceLinkId(data);
    setSourceAvailableBalance(balance);
  };

  return (
    <Box>
      <Grid gutter="lg">
        <Grid.Col span={12} md={7}>
          <Paper bg="white" p={20} radius={10}>
            {/* THÔNG TIN KHOẢN GỬI */}
            <Text fw={600} mb="sm">
              {t("DEPOSIT_INFORMATION")}
            </Text>

            <Grid gutter="md" align="flex-start">
              {resultWithdraw?.savingsAccountInfo &&
                resultWithdraw.savingsAccountInfo.map((item, index: number) => (
                  <Grid.Col span={12} md={6} key={item.key}>
                    <Flex justify="space-between">
                      <Text fz="sm" c="dimmed">
                        {beText(item.name)}
                      </Text>
                      <Text
                        fz="sm"
                        // VersionEn: so sánh với NHÃN do máy chủ trả, chỉ để in đậm/tô xanh.
                        // Chưa sửa được: javis-customer-service JsonUtil
                        // .convertObjectToDynamicField() set CẢ key và name = nhãn tiếng Việt
                        // trong MOBILE_CONFIG (bỏ entry.getKey()), nên FE không có mã ổn định
                        // nào để so. BE trả nhãn EN là mất in đậm — chỉ ảnh hưởng trình bày,
                        // không ảnh hưởng số liệu.
                        fw={item.key === "Số tiền gửi" ? 700 : 500}
                        c={item.key === "Số tiền gửi" ? "blue" : undefined}
                      >
                        {beText(item.value)}
                      </Text>
                    </Flex>
                  </Grid.Col>
                ))}
            </Grid>

            <Divider my="sm" variant="dashed" />

            {/* THÔNG TIN TẤT TOÁN */}
            <Text fw={600} my="20px">
              {t("ACCUMULATION_INFORMATION")}
            </Text>

            <Grid gutter="md" align="flex-start">
              {resultWithdraw?.transactionInfo &&
                resultWithdraw.transactionInfo.map((item, index: number) => (
                  <Grid.Col span={12} md={6} key={item.key}>
                    <Flex justify="space-between">
                      <Text fz="sm" c="dimmed">
                        {beText(item.name)}
                      </Text>
                      <Text
                        fz="sm"
                        fw={
                          // VersionEn: xem ghi chú so sánh nhãn ở trên.
                          item.key === "Tiền gửi tích lũy gần nhất" ? 700 : 500
                        }
                        c={
                          item.key === "Tiền gửi tích lũy gần nhất"
                            ? "blue"
                            : undefined
                        }
                      >
                        {beText(item.value)}
                      </Text>
                    </Flex>
                  </Grid.Col>
                ))}
            </Grid>
          </Paper>
          <Paper bg="white" p={20} radius={10} mt={20}>
            <Text fw={600} my="20px">
              {t("BENEFICIARY_ACCOUNT")}
            </Text>

            <BeneficiaryAccounts
              sourceAccount={dataSources}
              // onChangeSourceLinkId={(id) => setSourceLinkId(id)}
              onChangeSourceLinkId={handleReceiveData}
            />

            <NumberInput
              label={`${t("ACCUMULATED_DEPOSIT_THIS_TIME")}`}
              placeholder={`${t("INPUT_PLACEHOLDER_AMOUNT")} `}
              value={withdrawAmount}
              onChange={(value) => {
                setWithdrawAmount(value as number);
                if (value) {
                  setAmountError(null);
                }
              }}
              error={amountError}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              formatter={(value) =>
                !Number.isNaN(parseFloat(value))
                  ? `${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ".")
                  : ""
              }
              onKeyDown={(e) => {
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              rightSection={<Text fw={500}>VND</Text>}
              rightSectionWidth={48}
              styles={{
                input: {
                  textAlign: "left",
                  paddingRight: 56,
                },
                label: {
                  padding: "10px 0",
                },
              }}
            />
          </Paper>
        </Grid.Col>
      </Grid>
      <Group position="center" mt={rem(20)}>
        <Box
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            width: "calc(100% - var(--mantine-navbar-width, 0px))",
            padding: "10px 16px",
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
            boxShadow: "0px 5px 15px 0px #00000026",
            height: "76px",
            alignItems: "center",
            backgroundColor: "white",
            zIndex: 1,
          }}
        >
          <Flex>
            <Title fz={14}>{t("TOTAL_DEPOSIT_COLON")} </Title>
            <Title fz={14} c="#0171BB">
              {withdrawAmount ? `${toCurrency(withdrawAmount)} VND` : "0 VND"}
            </Title>
          </Flex>
          <Button
            type="submit"
            onClick={handleSubmit}
            loading={loadingInit}
            sx={() => ({
              width: "160px",
              backgroundColor: "#2A81D0",
              color: "white",
              borderRadius: "8px",
              padding: "8px 10px",
              cursor: "pointer",
              fontWeight: 400,
            })}
          >
            {t("NEXT")}
          </Button>
        </Box>
      </Group>
      <PopupNoti opened={popupOpened} onClose={closePopup} />
    </Box>
  );
};

export default DepositTopupStepOne;
